/**
 * ConfirmAddress — modal for resolving ambiguous geocoding results.
 *
 * Opened from the "?" badge in the address list when a location has
 * alternativeLocations (extra geocoder candidates). The user picks a
 * candidate on a mini-map and either updates only the coordinates
 * ("Update Coordinates") or also overwrites the stored address with the
 * candidate's formatted address ("Update Address"). `updatedInZone` is
 * recomputed so the marker turns green only if the pick lands inside the
 * zone circle.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import Map, { Marker } from "react-map-gl";
import * as turf from "@turf/turf";
import { Spin, Radio, Space } from "antd";

import { SubmitIcon } from "components/Icons";
import { PrimaryButton, SecondaryButton, TextButton } from "components/Buttons";
import markerIcons from "components/Markers/markerIcons";

import { doUpdateLocation } from "modules/location/stores/thunk";
import { doDetailZone } from "modules/zone/stores/thunk";

import { MAP_CONFIG, MAP_STYLES } from "../Main";

import "./ConfirmAddress.scss";

const viewport = {
  width: "100%",
  height: "100%",
  zoom: 10,
};

const ConfirmAddress = ({
  zone,
  location,
  circle,
  isFetching,
  onClosePopup,
}) => {
  const dispatch = useDispatch();
  const mapRef = useRef();
  // GF-63: NO preselection. Index 0 used to be the default, and for a review
  // candidate — a row that by definition has no stored position — index 0 is
  // the FIRST CANDIDATE, not a stored value. Pressing a button without touching
  // the radio group therefore saved a suggestion the operator never chose.
  //
  // That was survivable while the other candidates stayed behind as a way back.
  // GF-62's fix clears them, and the row then has a `lat`, so the geocode
  // button no longer picks it up either (`address && !lat`). The mistake became
  // unrecoverable, which is why the choice must now be deliberate: null means
  // "nothing selected", and both buttons stay disabled until it is not null.
  const [selectedLocationIdx, setSelectedLocationIdx] = useState(null);
  const {
    _id: locationId,
    lat: locationLat,
    formattedAddress: locationFormattedAddress,
    lng: locationLng,
    type: locationType,
    color: locationColor,
    orderNo,
    alternativeLocations,
  } = location || {};

  const locationOptions = useMemo(() => {
    // `alternativeLocations` kan vara null i databasen, inte bara tom eller
    // ifylld: callbacken skrev fältet ovillkorligt och Mongoose 5 gör undefined
    // till null i ett $set (GF-60). Sju av 103 dokument i dev-DB bar redan null
    // när det mättes. En spread av null kastar TypeError och tar hela modalen.
    const candidates = alternativeLocations || [];
    // Den lagrade positionen är bara ett alternativ när den finns. Tidigare låg
    // den alltid först i listan, så en adress utan koordinat fick en radioknapp
    // utan etikett (formattedAddress var undefined) som vid val skrev
    // undefined-koordinater till servern. Med granskningskandidater är det
    // normalfallet, inte ett undantag.
    const stored =
      locationLat != null && locationLng != null
        ? [{ lat: locationLat, lng: locationLng, formattedAddress: locationFormattedAddress }]
        : [];
    return [...stored, ...candidates];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alternativeLocations, locationLat, locationLng, locationFormattedAddress]);

  // Stop any camera easing before this component goes away.
  //
  // handleChangeAltLocation starts a 2000 ms flyTo. Closing the modal inside
  // those two seconds — which is the normal rhythm: pick a candidate, press
  // save — leaves mapbox-gl with `_moving === true`. reuseMaps then hands the
  // same instance to the NEXT row, and react-map-gl's reuse() skips reapplying
  // initialViewState while the map reports movement, so row B can open showing
  // row A's viewport. That is the same leak between rows that destroyOnClose
  // was added to stop, one layer down.
  //
  // NOT VERIFIED IN A BROWSER. The cleanup cannot make things worse — if the
  // map is already recycled the ref is gone and the catch swallows it — but
  // whether React runs this before react-map-gl's own unmount handler is an
  // ordering detail that needs a real browser to settle. See GF-66.
  useEffect(() => () => {
    try { mapRef.current?.stop(); } catch (e) { /* already torn down */ }
  }, []);

  const handleChangeAltLocation = (e) => {
    const selectedIdx = e.target.value;
    const selectedLocation = locationOptions[selectedIdx];
    setSelectedLocationIdx(selectedIdx);
    if (selectedLocation?.lat) {
      mapRef.current.flyTo({
        center: [selectedLocation.lng, selectedLocation.lat],
        duration: 2000,
      });
    }
  };

  const handleConfirm = async (updateAll = false) => {
    // GF-65: STRICT comparison, and it is load-bearing — do not "simplify" it
    // to `if (updateAll)`. A handler referenced directly (`onClick={handleConfirm}`)
    // receives React's SyntheticEvent as its first argument, and an object is
    // truthy. That is exactly how "Update Coordinates" silently became a second
    // "Update Address" button for three years: both wrote the provider's
    // formattedAddress over the operator's own text, with no way back.
    //
    // The call sites below now pass an explicit boolean, so this line is not
    // what fixes the bug — it is what stops the bug from coming back if someone
    // reintroduces a direct reference. tools/confirm-address/confirm-unit-test.js
    // FAILS if this reverts to truthiness.
    const shouldUpdateAddress = updateAll === true;
    try {
      const selectedLocation = locationOptions[selectedLocationIdx];
      // Koordinaten måste finnas innan den skickas: turf.point([undefined,
      // undefined]) kastar, och ett halvt ifyllt alternativ skulle annars
      // skriva undefined-koordinater till servern. Listan bygger bara poster
      // med koordinat i dag, men den läser data som är äldre än den regeln.
      //
      // GF-62, review pass 2: the guard tests truthiness, not `!= null`.
      // The three places that decide whether a row HAS A POSITION all read
      // `lat` alone and all read it as a boolean — ZonesDetails.jsx:181
      // (`if (s.lat)`), formatLocationPlaces() (`!location.lat`) and the
      // geocode callback (`else if (place.lat)`). A candidate at lat 0 or
      // lng 0 is a provider artefact, never a Swedish address (the country
      // spans roughly 55-69 N, 11-24 E), and letting it through stored a
      // position that all three still render as "Saknar position".
      // Rejecting it HERE is what lets the clearing below stay unconditional:
      // past this guard both coordinates are real, so there is no state where
      // the row counts as positioned in one place and unpositioned in another.
      //
      // NOTE: the `!= null` tests further down (lines 68, 156, 159) are a
      // different question — "is there a coordinate to draw/offer" — and were
      // deliberately left alone. A stored row with lat 0 therefore still gets
      // a preselected radio option that this guard then refuses, which is a
      // silent no-op for the operator. Cosmetic, logged rather than fixed here
      // because changing it reopens the review for no behavioural gain.
      if (selectedLocation?.lat && selectedLocation?.lng) {
        const { lat, lng, formattedAddress } = selectedLocation;
        const point = turf.point([lng, lat]);
        const polygon = turf.polygon([...circle.geometry.coordinates]);
        const payload = {
          id: locationId,
          lat,
          lng,
          updatedInZone: turf.booleanPointInPolygon(point, polygon),
          // ⚠️ DEAD SINCE 2026-09-16 — THIS LINE NO LONGER DOES ANYTHING.
          //
          // `alternativeLocations` was removed from the backend's update
          // whitelist, so this value is dropped before it reaches the database.
          // The clearing now happens server-side in updateLocation's candidate
          // invariant, unconditionally whenever a truthy numeric `lat` is
          // written — which the guard above already guarantees for every
          // payload this function sends. Behaviour is unchanged; ownership of
          // the rule moved.
          //
          // Left in place deliberately rather than removed in that pass:
          // deleting it is a frontend change needing its own deploy (and
          // GF-41's cache trap), and nothing depends on it either way. Remove
          // it when this file is next touched for a real reason.
          //
          // What this comment used to claim, and why each part is now wrong —
          // kept because the reasoning was cited from the backend and someone
          // will come looking for it:
          //   · "belongs here rather than as a blanket rule in updateLocation()"
          //     — it IS a blanket rule there now, and that is the fix.
          //   · "would drop candidates that are still needed" — it does not:
          //     the server requires a real number, so a row without a position
          //     keeps its candidates.
          //   · "enabling a whitelisting ValidationPipe WILL bring GF-62 back"
          //     — no longer true for THIS field. It remains true for
          //     `updatedInZone` above, which has no server-side counterpart.
          //     Note the nuance: a pipe that STRIPS this field changes nothing,
          //     but `forbidNonWhitelisted` — the variant GF-02 favours — still
          //     rejects the whole payload with 400 until the DTO is fixed. That
          //     is covered by the `id` point in GF-02's checklist, not here.
          alternativeLocations: [],
        };
        if (shouldUpdateAddress) payload.address = formattedAddress;
        await dispatch(doUpdateLocation(payload));
        await dispatch(doDetailZone(zone._id));
      }
    } finally {
      onClosePopup();
    }
  };

  const markerCoord = useMemo(() => {
    const selectedLocation = locationOptions[selectedLocationIdx];
    return {
      lat: selectedLocation?.lat || locationLat,
      lng: selectedLocation?.lng || locationLng,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocationIdx, locationLat, locationLng]);

  // Kartans startvy. `locationLat`/`locationLng` är undefined för just de
  // adresser som har en granskningskandidat — de saknar per definition
  // position — och vad react-map-gl gör med undefined i initialViewState är
  // inte dokumenterat och inte värt att lita på. Faller därför tillbaka på
  // första alternativet som har en koordinat, och sist på zonens centrum, som
  // alltid finns.
  const initialCenter = useMemo(() => {
    if (locationLat != null && locationLng != null) {
      return { latitude: locationLat, longitude: locationLng };
    }
    const withCoordinate = locationOptions.find((o) => o?.lat != null && o?.lng != null);
    if (withCoordinate) {
      return { latitude: withCoordinate.lat, longitude: withCoordinate.lng };
    }
    return { latitude: zone?.centerLat, longitude: zone?.centerLng };
  }, [locationLat, locationLng, locationOptions, zone]);

  return (
    <Spin spinning={isFetching}>
      <div className="confirm-address">
        <div className="main-content">
          <div className="left-content">
            {/* UI-text är svenska — appen är svensk för användarna även om kod
                och kommentarer är engelska. Dessa fyra strängar var rester från
                leverantören; övriga engelska strängar i appen tas i ett eget
                språkpass. */}
            <h5>Bekräfta adress</h5>
            <label style={{ marginBottom: 12, display: "block" }}>
              Välj adress
            </label>
            <Radio.Group
              onChange={handleChangeAltLocation}
              value={selectedLocationIdx}
            >
              <Space direction="vertical">
                {locationOptions?.map(({ formattedAddress }, idx) => (
                  <Radio key={`alt-location-option-${idx}`} value={idx}>
                    {formattedAddress}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </div>
          <Map
            style={{ height: 360, width: "50%", borderRadius: 8 }}
            ref={mapRef}
            // GF-63 follow-up: the modal now unmounts on close (destroyOnClose
            // in Main.jsx), which is what makes "nothing is preselected" hold
            // for every row rather than only the first. The cost is that this
            // Map is rebuilt per opening instead of once per page visit, and
            // mapbox-gl bills a map load per `new Map()` — an operator working
            // through the ~514 review rows would go from 1 load to ~514.
            // reuseMaps keeps the underlying instance across mounts.
            reuseMaps
            initialViewState={{
              ...initialCenter,
              ...viewport,
            }}
            mapStyle={MAP_STYLES.standard}
            {...MAP_CONFIG}
          >
            {markerCoord?.lat && (
              <Marker
                latitude={markerCoord.lat}
                longitude={markerCoord.lng}
                style={{ zIndex: 9 }}
              >
                <div className="marker">
                  <img src={markerIcons[locationType][locationColor]} alt="" />
                  <div
                    className="marker-info"
                    style={{
                      color: "white",
                      fontSize: "10px",
                      fontWeight: "bold",
                    }}
                  >
                    {orderNo}
                  </div>
                </div>
              </Marker>
            )}
            {locationOptions.map((altLocation, idx) => {
              if (
                !altLocation.lat ||
                (altLocation.lat === markerCoord.lat &&
                  altLocation.lng === markerCoord.lng)
              )
                return null;
              return (
                <Marker
                  key={`alt-location-${idx}`}
                  latitude={altLocation.lat}
                  longitude={altLocation.lng}
                  style={{ zIndex: 9 }}
                >
                  <div className="marker">
                    <img
                      src={markerIcons["alternativeLocation"]["888888"]}
                      alt=""
                    />
                  </div>
                </Marker>
              );
            })}
          </Map>
        </div>
        <div className="bottom-buttons">
          <TextButton label="Avbryt" onClick={onClosePopup} />
          {/*
            GF-65: BOTH handlers pass an explicit boolean. `onClick={handleConfirm}`
            — a direct reference — hands React's SyntheticEvent to `updateAll`,
            which made this button write the address just like the one beside it.
            Keep the arrow functions even though the left one looks redundant.

            GF-63: disabled until the operator has actually chosen something.
          */}
          {/* Etiketterna säger vad som HÄNDER, inte vilket fält som uppdateras.
              "Update Coordinates" / "Update Address" beskrev mekaniken och dolde
              det som spelar roll: den ena knappen ersätter operatörens egen
              adressbenämning med leverantörens, oåterkalleligt. Att skillnaden
              var osynlig är en del av varför GF-65 kunde leva i tre år. En knapp
              som förstör något ska säga det. */}
          <SecondaryButton
            type="primary"
            label="Spara position"
            icon={<SubmitIcon />}
            disabled={selectedLocationIdx === null}
            onClick={() => handleConfirm(false)}
          />
          <PrimaryButton
            size="lg"
            type="primary"
            label="Spara position och ersätt adressen"
            icon={<SubmitIcon />}
            disabled={selectedLocationIdx === null}
            onClick={() => handleConfirm(true)}
          />
        </div>
      </div>
    </Spin>
  );
};

export default ConfirmAddress;
